import { useEffect, useMemo, useRef } from "react";
import { mat4, quat, vec2, vec3, vec4 } from "gl-matrix";
import "./InfiniteMenu.css";

const discVertShaderSource = `#version 300 es

uniform mat4 uWorldMatrix;
uniform mat4 uViewMatrix;
uniform mat4 uProjectionMatrix;
uniform vec3 uCameraPosition;
uniform vec4 uRotationAxisVelocity;

in vec3 aModelPosition;
in vec3 aModelNormal;
in vec2 aModelUvs;
in mat4 aInstanceMatrix;
in vec3 aInstanceColor;
in float aInstanceItemIndex;

out vec2 vUvs;
out float vAlpha;
flat out int vInstanceId;
flat out int vItemIndex;
out vec3 vInstanceColor;

#define PI 3.141593

void main() {
    vec4 worldPosition = uWorldMatrix * aInstanceMatrix * vec4(aModelPosition, 1.);

    vec3 centerPos = (uWorldMatrix * aInstanceMatrix * vec4(0., 0., 0., 1.)).xyz;
    float radius = length(centerPos.xyz);

    if (gl_VertexID > 0) {
        vec3 rotationAxis = uRotationAxisVelocity.xyz;
        float rotationVelocity = min(.15, uRotationAxisVelocity.w * 15.);
        vec3 stretchDir = normalize(cross(centerPos, rotationAxis));
        vec3 relativeVertexPos = normalize(worldPosition.xyz - centerPos);
        float strength = dot(stretchDir, relativeVertexPos);
        float invAbsStrength = min(0., abs(strength) - 1.);
        strength = rotationVelocity * sign(strength) * abs(invAbsStrength * invAbsStrength * invAbsStrength + 1.);
        worldPosition.xyz += stretchDir * strength;
    }

    worldPosition.xyz = radius * normalize(worldPosition.xyz);

    gl_Position = uProjectionMatrix * uViewMatrix * worldPosition;

    vAlpha = smoothstep(0.5, 1., normalize(worldPosition.xyz).z) * .9 + .1;
    vUvs = aModelUvs;
    vInstanceId = gl_InstanceID;
    vItemIndex = int(aInstanceItemIndex + 0.5);
    vInstanceColor = aInstanceColor;
}
`;

const discFragShaderSource = `#version 300 es
precision highp float;

uniform sampler2D uTex;
uniform int uItemCount;
uniform int uAtlasSize;
uniform vec2 uPointer;
uniform float uFrames;

out vec4 outColor;

in vec2 vUvs;
in float vAlpha;
flat in int vInstanceId;
flat in int vItemIndex;
in vec3 vInstanceColor;

void main() {
    int itemIndex = vItemIndex % max(1, uItemCount);
    int cellsPerRow = uAtlasSize;
    int cellX = itemIndex % cellsPerRow;
    int cellY = itemIndex / cellsPerRow;
    vec2 cellSize = vec2(1.0) / vec2(float(cellsPerRow));
    vec2 cellOffset = vec2(float(cellX), float(cellY)) * cellSize;

    ivec2 texSize = textureSize(uTex, 0);
    float imageAspect = float(texSize.x) / float(texSize.y);
    float containerAspect = 1.0;

    float scale = max(imageAspect / containerAspect,
                     containerAspect / imageAspect);

    vec2 st = vec2(vUvs.x, 1.0 - vUvs.y);
    st = (st - 0.5) * scale + 0.5;

    st = clamp(st, 0.0, 1.0);

    st = st * cellSize + cellOffset;

    vec4 texColor = texture(uTex, st);
    vec3 tint = vInstanceColor;
    vec3 base = mix(texColor.rgb, tint, 0.35);

    float pointerDist = distance(vUvs, uPointer);
    float glare = smoothstep(0.55, 0.0, pointerDist);

    float sweep = sin((vUvs.x + vUvs.y + uFrames * 0.015) * 6.283);
    float sheen = smoothstep(-0.2, 0.8, sweep);

    float rim = smoothstep(0.32, 0.5, distance(vUvs, vec2(0.5)));

    vec3 highlight = tint * (0.45 * glare) + vec3(1.0) * (0.15 * sheen * glare);
    vec3 rimGlow = tint * rim * 0.2;

    vec3 color = min(base + highlight + rimGlow, vec3(1.0));
    outColor = vec4(color, texColor.a * vAlpha);
}
`;

const planetVertShaderSource = `#version 300 es

uniform mat4 uWorldMatrix;
uniform mat4 uViewMatrix;
uniform mat4 uProjectionMatrix;

in vec3 aModelPosition;
in vec3 aModelNormal;
in vec2 aModelUvs;
in mat4 aInstanceMatrix;
in vec3 aInstanceColor;
in float aInstanceItemIndex;

out vec2 vUvs;
out float vAlpha;
out vec3 vWorldPosition;
out vec3 vWorldNormal;
flat out int vItemIndex;
out vec3 vInstanceColor;

void main() {
    mat4 modelMatrix = uWorldMatrix * aInstanceMatrix;
    vec4 worldPosition = modelMatrix * vec4(aModelPosition, 1.0);
    vec4 centerPosition = modelMatrix * vec4(0.0, 0.0, 0.0, 1.0);

    vWorldPosition = worldPosition.xyz;
    vWorldNormal = normalize(mat3(modelMatrix) * aModelNormal);

    vec3 centerDir = normalize(centerPosition.xyz);
    vAlpha = smoothstep(-0.15, 0.35, centerDir.z) * 0.9 + 0.1;

    vUvs = aModelUvs;
    vItemIndex = int(aInstanceItemIndex + 0.5);
    vInstanceColor = aInstanceColor;

    gl_Position = uProjectionMatrix * uViewMatrix * worldPosition;
}
`;

const planetFragShaderSource = `#version 300 es
precision highp float;

uniform sampler2D uTex;
uniform int uItemCount;
uniform int uAtlasSize;
uniform vec3 uCameraPosition;
uniform float uFrames;

out vec4 outColor;

in vec2 vUvs;
in float vAlpha;
in vec3 vWorldPosition;
in vec3 vWorldNormal;
flat in int vItemIndex;
in vec3 vInstanceColor;

float saturate(float v) {
    return clamp(v, 0.0, 1.0);
}

void main() {
    int itemIndex = vItemIndex % max(1, uItemCount);
    int cellsPerRow = uAtlasSize;
    int cellX = itemIndex % cellsPerRow;
    int cellY = itemIndex / cellsPerRow;
    vec2 cellSize = vec2(1.0) / vec2(float(cellsPerRow));
    vec2 cellOffset = vec2(float(cellX), float(cellY)) * cellSize;

    vec2 st = vec2(fract(vUvs.x), 1.0 - vUvs.y);
    st = clamp(st, 0.0, 1.0);
    st = st * cellSize + cellOffset;

    vec4 texColor = texture(uTex, st);
    vec3 albedo = mix(texColor.rgb, vInstanceColor, 0.18);

    vec3 normal = normalize(vWorldNormal);
    vec3 lightDir = normalize(vec3(-0.35, 0.25, 0.92));
    vec3 viewDir = normalize(uCameraPosition - vWorldPosition);

    float diff = max(dot(normal, lightDir), 0.0);
    vec3 halfDir = normalize(lightDir + viewDir);
    float spec = pow(max(dot(normal, halfDir), 0.0), 64.0);

    float fresnel = pow(1.0 - saturate(dot(normal, viewDir)), 3.0);
    float pulse = 0.6 + 0.4 * sin(uFrames * 0.012 + float(itemIndex) * 1.9);
    vec3 rim = vInstanceColor * fresnel * (0.22 + 0.18 * pulse);

    vec3 color = albedo * (0.2 + diff * 0.9) + rim + vec3(1.0) * spec * 0.22;
    outColor = vec4(color, vAlpha);
}
`;

const sunVertShaderSource = `#version 300 es
uniform mat4 uWorldMatrix;
uniform mat4 uViewMatrix;
uniform mat4 uProjectionMatrix;
uniform float uScale;

in vec3 aModelPosition;
in vec2 aModelUvs;

out vec2 vUvs;

void main() {
    vec3 scaledPosition = aModelPosition * uScale;
    vec4 worldPosition = uWorldMatrix * vec4(scaledPosition, 1.0);
    gl_Position = uProjectionMatrix * uViewMatrix * worldPosition;
    vUvs = aModelUvs;
}
`;

const sunFragShaderSource = `#version 300 es
precision highp float;

uniform float uTime;
uniform float uIntensity;
uniform vec3 uColor;
uniform vec3 uCoreColor;

in vec2 vUvs;

out vec4 outColor;

float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 34.45);
    return fract(p.x * p.y);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
    float v = 0.0;
    float amp = 0.55;
    mat2 rot = mat2(0.82, 0.57, -0.57, 0.82);
    for (int i = 0; i < 4; i++) {
        v += amp * noise(p);
        p = rot * p * 2.02 + 1.7;
        amp *= 0.5;
    }
    return v;
}

void main() {
    vec2 uv = vUvs * 2.0 - 1.0;
    float r = length(uv);

    float edge = 1.0 - smoothstep(0.88, 1.0, r);
    float core = 1.0 - smoothstep(0.0, 0.48, r);
    float corona = 1.0 - smoothstep(0.15, 1.0, r);

    float angle = atan(uv.y, uv.x);
    float rays = abs(sin(angle * 3.5 + uTime * 0.45));
    rays = smoothstep(0.2, 0.95, rays);

    float swirl = fbm(uv * 3.2 + vec2(uTime * 0.15, -uTime * 0.12));
    float flare = fbm(uv * 6.4 - vec2(uTime * 0.08, uTime * 0.05));
    float turbulence = mix(swirl, flare, 0.55);

    float pulse = 0.72 + 0.28 * sin(uTime * 1.4);
    float glow = core * 1.1 + corona * (0.55 + turbulence * 0.45) + rays * 0.06 * corona;

    vec3 color = mix(uColor, uCoreColor, core);
    color *= glow * (0.6 + 0.4 * pulse);

    float alpha = edge * uIntensity * clamp(glow, 0.0, 1.4) * pulse;
    outColor = vec4(color, alpha);
}
`;

class Face {
  constructor(a, b, c) {
    this.a = a;
    this.b = b;
    this.c = c;
  }
}

class Vertex {
  constructor(x, y, z) {
    this.position = vec3.fromValues(x, y, z);
    this.normal = vec3.create();
    this.uv = vec2.create();
  }
}

class Geometry {
  constructor() {
    this.vertices = [];
    this.faces = [];
  }

  addVertex(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.vertices.push(new Vertex(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  addFace(...args) {
    for (let i = 0; i < args.length; i += 3) {
      this.faces.push(new Face(args[i], args[i + 1], args[i + 2]));
    }
    return this;
  }

  get lastVertex() {
    return this.vertices[this.vertices.length - 1];
  }

  subdivide(divisions = 1) {
    const midPointCache = {};
    let f = this.faces;

    for (let div = 0; div < divisions; ++div) {
      const newFaces = new Array(f.length * 4);

      f.forEach((face, ndx) => {
        const mAB = this.getMidPoint(face.a, face.b, midPointCache);
        const mBC = this.getMidPoint(face.b, face.c, midPointCache);
        const mCA = this.getMidPoint(face.c, face.a, midPointCache);

        const i = ndx * 4;
        newFaces[i + 0] = new Face(face.a, mAB, mCA);
        newFaces[i + 1] = new Face(face.b, mBC, mAB);
        newFaces[i + 2] = new Face(face.c, mCA, mBC);
        newFaces[i + 3] = new Face(mAB, mBC, mCA);
      });

      f = newFaces;
    }

    this.faces = f;
    return this;
  }

  spherize(radius = 1) {
    this.vertices.forEach((vertex) => {
      vec3.normalize(vertex.normal, vertex.position);
      vec3.scale(vertex.position, vertex.normal, radius);
    });
    return this;
  }

  computeSphericalUVs() {
    this.vertices.forEach((vertex) => {
      const x = vertex.normal[0];
      const y = vertex.normal[1];
      const z = vertex.normal[2];
      const u = Math.atan2(z, x) / (2 * Math.PI) + 0.5;
      const v = Math.asin(Math.max(-1, Math.min(1, y))) / Math.PI + 0.5;
      vertex.uv[0] = u;
      vertex.uv[1] = v;
    });
    return this;
  }

  get data() {
    return {
      vertices: this.vertexData,
      indices: this.indexData,
      normals: this.normalData,
      uvs: this.uvData,
    };
  }

  get vertexData() {
    return new Float32Array(
      this.vertices.flatMap((v) => Array.from(v.position)),
    );
  }

  get normalData() {
    return new Float32Array(this.vertices.flatMap((v) => Array.from(v.normal)));
  }

  get uvData() {
    return new Float32Array(this.vertices.flatMap((v) => Array.from(v.uv)));
  }

  get indexData() {
    return new Uint16Array(this.faces.flatMap((f) => [f.a, f.b, f.c]));
  }

  getMidPoint(ndxA, ndxB, cache) {
    const cacheKey = ndxA < ndxB ? `k_${ndxB}_${ndxA}` : `k_${ndxA}_${ndxB}`;
    if (Object.prototype.hasOwnProperty.call(cache, cacheKey)) {
      return cache[cacheKey];
    }
    const a = this.vertices[ndxA].position;
    const b = this.vertices[ndxB].position;
    const ndx = this.vertices.length;
    cache[cacheKey] = ndx;
    this.addVertex(
      (a[0] + b[0]) * 0.5,
      (a[1] + b[1]) * 0.5,
      (a[2] + b[2]) * 0.5,
    );
    return ndx;
  }
}

class IcosahedronGeometry extends Geometry {
  constructor() {
    super();
    const t = Math.sqrt(5) * 0.5 + 0.5;
    this.addVertex(
      -1,
      t,
      0,
      1,
      t,
      0,
      -1,
      -t,
      0,
      1,
      -t,
      0,
      0,
      -1,
      t,
      0,
      1,
      t,
      0,
      -1,
      -t,
      0,
      1,
      -t,
      t,
      0,
      -1,
      t,
      0,
      1,
      -t,
      0,
      -1,
      -t,
      0,
      1,
    ).addFace(
      0,
      11,
      5,
      0,
      5,
      1,
      0,
      1,
      7,
      0,
      7,
      10,
      0,
      10,
      11,
      1,
      5,
      9,
      5,
      11,
      4,
      11,
      10,
      2,
      10,
      7,
      6,
      7,
      1,
      8,
      3,
      9,
      4,
      3,
      4,
      2,
      3,
      2,
      6,
      3,
      6,
      8,
      3,
      8,
      9,
      4,
      9,
      5,
      2,
      4,
      11,
      6,
      2,
      10,
      8,
      6,
      7,
      9,
      8,
      1,
    );
  }
}

class DiscGeometry extends Geometry {
  constructor(steps = 4, radius = 1) {
    super();
    const resolvedSteps = Math.max(4, steps);

    const alpha = (2 * Math.PI) / resolvedSteps;

    this.addVertex(0, 0, 0);
    this.lastVertex.uv[0] = 0.5;
    this.lastVertex.uv[1] = 0.5;

    for (let i = 0; i < resolvedSteps; ++i) {
      const x = Math.cos(alpha * i);
      const y = Math.sin(alpha * i);
      this.addVertex(radius * x, radius * y, 0);
      this.lastVertex.uv[0] = x * 0.5 + 0.5;
      this.lastVertex.uv[1] = y * 0.5 + 0.5;

      if (i > 0) {
        this.addFace(0, i, i + 1);
      }
    }
    this.addFace(0, resolvedSteps, 1);
  }
}

function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);

  if (success) {
    return shader;
  }

  console.error(gl.getShaderInfoLog(shader));
  gl.deleteShader(shader);
  return null;
}

function createProgram(
  gl,
  shaderSources,
  transformFeedbackVaryings,
  attribLocations,
) {
  const program = gl.createProgram();

  [gl.VERTEX_SHADER, gl.FRAGMENT_SHADER].forEach((type, ndx) => {
    const shader = createShader(gl, type, shaderSources[ndx]);
    if (shader) gl.attachShader(program, shader);
  });

  if (transformFeedbackVaryings) {
    gl.transformFeedbackVaryings(
      program,
      transformFeedbackVaryings,
      gl.SEPARATE_ATTRIBS,
    );
  }

  if (attribLocations) {
    for (const attrib in attribLocations) {
      gl.bindAttribLocation(program, attribLocations[attrib], attrib);
    }
  }

  gl.linkProgram(program);
  const success = gl.getProgramParameter(program, gl.LINK_STATUS);

  if (success) {
    return program;
  }

  console.error(gl.getProgramInfoLog(program));
  gl.deleteProgram(program);
  return null;
}

function makeVertexArray(gl, bufLocNumElmPairs, indices) {
  const va = gl.createVertexArray();
  gl.bindVertexArray(va);

  for (const [buffer, loc, numElem] of bufLocNumElmPairs) {
    if (loc === -1) continue;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, numElem, gl.FLOAT, false, 0, 0);
  }

  if (indices) {
    const indexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      new Uint16Array(indices),
      gl.STATIC_DRAW,
    );
  }

  gl.bindVertexArray(null);
  return va;
}

function resizeCanvasToDisplaySize(canvas) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const displayWidth = Math.round(canvas.clientWidth * dpr);
  const displayHeight = Math.round(canvas.clientHeight * dpr);
  const needResize =
    canvas.width !== displayWidth || canvas.height !== displayHeight;
  if (needResize) {
    canvas.width = displayWidth;
    canvas.height = displayHeight;
  }
  return needResize;
}

function makeBuffer(gl, sizeOrData, usage) {
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, sizeOrData, usage);
  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  return buf;
}

function createAndSetupTexture(gl, minFilter, magFilter, wrapS, wrapT) {
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapS);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magFilter);
  return texture;
}

function hexToRgb01(hex) {
  if (!hex) return null;
  const normalized = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(normalized)) return null;

  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16) / 255,
    g: parseInt(expanded.slice(2, 4), 16) / 255,
    b: parseInt(expanded.slice(4, 6), 16) / 255,
  };
}

class ArcballControl {
  isPointerDown = false;
  orientation = quat.create();
  pointerRotation = quat.create();
  rotationVelocity = 0;
  rotationAxis = vec3.fromValues(1, 0, 0);
  snapDirection = vec3.fromValues(0, 0, 1);
  snapTargetDirection;
  EPSILON = 0.1;
  IDENTITY_QUAT = quat.create();

  constructor(canvas, updateCallback) {
    this.canvas = canvas;
    this.updateCallback = updateCallback || (() => null);

    this.pointerPos = vec2.create();
    this.previousPointerPos = vec2.create();
    this._rotationVelocity = 0;
    this._combinedQuat = quat.create();

    this.handlePointerDown = (e) => {
      vec2.set(this.pointerPos, e.clientX, e.clientY);
      vec2.copy(this.previousPointerPos, this.pointerPos);
      this.isPointerDown = true;
    };
    this.handlePointerUp = () => {
      this.isPointerDown = false;
    };
    this.handlePointerLeave = () => {
      this.isPointerDown = false;
    };
    this.handlePointerMove = (e) => {
      if (this.isPointerDown) {
        vec2.set(this.pointerPos, e.clientX, e.clientY);
      }
    };

    canvas.addEventListener("pointerdown", this.handlePointerDown);
    canvas.addEventListener("pointerup", this.handlePointerUp);
    canvas.addEventListener("pointerleave", this.handlePointerLeave);
    canvas.addEventListener("pointermove", this.handlePointerMove);

    canvas.style.touchAction = "none";
  }

  destroy() {
    if (!this.canvas) return;
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.canvas.removeEventListener("pointerleave", this.handlePointerLeave);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.style.touchAction = "";
  }

  update(deltaTime, targetFrameDuration = 16) {
    const timeScale = deltaTime / targetFrameDuration + 0.00001;
    let angleFactor = timeScale;
    const snapRotation = quat.create();

    if (this.isPointerDown) {
      const INTENSITY = 0.3 * timeScale;
      const ANGLE_AMPLIFICATION = 5 / timeScale;

      const midPointerPos = vec2.sub(
        vec2.create(),
        this.pointerPos,
        this.previousPointerPos,
      );
      vec2.scale(midPointerPos, midPointerPos, INTENSITY);

      if (vec2.sqrLen(midPointerPos) > this.EPSILON) {
        vec2.add(midPointerPos, this.previousPointerPos, midPointerPos);

        const p = this.#project(midPointerPos);
        const q = this.#project(this.previousPointerPos);
        const a = vec3.normalize(vec3.create(), p);
        const b = vec3.normalize(vec3.create(), q);

        vec2.copy(this.previousPointerPos, midPointerPos);

        angleFactor *= ANGLE_AMPLIFICATION;

        this.quatFromVectors(a, b, this.pointerRotation, angleFactor);
      } else {
        quat.slerp(
          this.pointerRotation,
          this.pointerRotation,
          this.IDENTITY_QUAT,
          INTENSITY,
        );
      }
    } else {
      const INTENSITY = 0.1 * timeScale;
      quat.slerp(
        this.pointerRotation,
        this.pointerRotation,
        this.IDENTITY_QUAT,
        INTENSITY,
      );

      if (this.snapTargetDirection) {
        const SNAPPING_INTENSITY = 0.2;
        const a = this.snapTargetDirection;
        const b = this.snapDirection;
        const sqrDist = vec3.squaredDistance(a, b);
        const distanceFactor = Math.max(0.1, 1 - sqrDist * 10);
        angleFactor *= SNAPPING_INTENSITY * distanceFactor;
        this.quatFromVectors(a, b, snapRotation, angleFactor);
      }
    }

    const combinedQuat = quat.multiply(
      quat.create(),
      snapRotation,
      this.pointerRotation,
    );
    this.orientation = quat.multiply(
      quat.create(),
      combinedQuat,
      this.orientation,
    );
    quat.normalize(this.orientation, this.orientation);

    const RA_INTENSITY = 0.8 * timeScale;
    quat.slerp(
      this._combinedQuat,
      this._combinedQuat,
      combinedQuat,
      RA_INTENSITY,
    );
    quat.normalize(this._combinedQuat, this._combinedQuat);

    const rad = Math.acos(this._combinedQuat[3]) * 2.0;
    const s = Math.sin(rad / 2.0);
    let rv = 0;
    if (s > 0.000001) {
      rv = rad / (2 * Math.PI);
      this.rotationAxis[0] = this._combinedQuat[0] / s;
      this.rotationAxis[1] = this._combinedQuat[1] / s;
      this.rotationAxis[2] = this._combinedQuat[2] / s;
    }

    const RV_INTENSITY = 0.5 * timeScale;
    this._rotationVelocity += (rv - this._rotationVelocity) * RV_INTENSITY;
    this.rotationVelocity = this._rotationVelocity / timeScale;

    this.updateCallback(deltaTime);
  }

  quatFromVectors(a, b, out, angleFactor = 1) {
    const axis = vec3.cross(vec3.create(), a, b);
    vec3.normalize(axis, axis);
    const d = Math.max(-1, Math.min(1, vec3.dot(a, b)));
    const angle = Math.acos(d) * angleFactor;
    quat.setAxisAngle(out, axis, angle);
    return { q: out, axis, angle };
  }

  #project(pos) {
    const r = 2;
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    const s = Math.max(w, h) - 1;

    const x = (2 * pos[0] - w - 1) / s;
    const y = (2 * pos[1] - h - 1) / s;
    let z = 0;
    const xySq = x * x + y * y;
    const rSq = r * r;

    if (xySq <= rSq / 2.0) {
      z = Math.sqrt(rSq - xySq);
    } else {
      z = rSq / Math.sqrt(xySq);
    }
    return vec3.fromValues(-x, y, z);
  }
}

class InfiniteGridMenu {
  TARGET_FRAME_DURATION = 1000 / 60;
  SPHERE_RADIUS = 2;

  #time = 0;
  #deltaTime = 0;
  #deltaFrames = 0;
  #frames = 0;

  camera = {
    matrix: mat4.create(),
    near: 0.1,
    far: 40,
    fov: Math.PI / 4,
    aspect: 1,
    position: vec3.fromValues(0, 0, 3),
    up: vec3.fromValues(0, 1, 0),
    matrices: {
      view: mat4.create(),
      projection: mat4.create(),
      inversProjection: mat4.create(),
    },
  };

  nearestVertexIndex = null;
  activeBaseIndex = null;
  smoothRotationVelocity = 0;
  scaleFactor = 1.0;
  baseCameraDistance = 3;
  movementActive = false;
  pointer = vec2.fromValues(0.5, 0.5);
  sunIntensity = 0;
  sunTargetIntensity = 0;
  sunScale = 0.85;
  sunHoldUntil = 0;

  constructor(
    canvas,
    items,
    onActiveItemChange,
    onMovementChange,
    onInit = null,
    scale = 1.0,
    variant = "disc",
    initialActiveItemId = null,
  ) {
    this.canvas = canvas;
    this.baseItems = items || [];
    this.initialActiveItemId = initialActiveItemId;
    this.onActiveItemChange = onActiveItemChange || (() => {});
    this.onMovementChange = onMovementChange || (() => {});
    this.scaleFactor = scale;
    this.variant = variant;
    this.baseCameraDistance = (this.variant === "sphere" ? 3.8 : 3) * scale;
    this.camera.position[2] = this.baseCameraDistance;
    this.sunScale = 0.85 * scale;
    this.sunScaleDynamic = this.sunScale;
    this.sunColor = { r: 1.0, g: 0.67, b: 0.25 };
    this.sunCoreColor = { r: 1.0, g: 0.88, b: 0.55 };
    this._raf = null;
    this._disposed = false;
    this.#init(onInit);
  }

  resize() {
    this.viewportSize = vec2.set(
      this.viewportSize || vec2.create(),
      this.canvas.clientWidth,
      this.canvas.clientHeight,
    );

    const gl = this.gl;
    const needsResize = resizeCanvasToDisplaySize(gl.canvas);
    if (needsResize) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    }

    this.#updateProjectionMatrix(gl);
  }

  setPointer(x, y) {
    const clampedX = Math.min(1, Math.max(0, x));
    const clampedY = Math.min(1, Math.max(0, y));
    this.pointer[0] = clampedX;
    this.pointer[1] = clampedY;
  }

  pick(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const x = (clientX - rect.left) / rect.width;
    const y = 1 - (clientY - rect.top) / rect.height;
    const ndcX = x * 2 - 1;
    const ndcY = y * 2 - 1;

    const clip = vec4.fromValues(ndcX, ndcY, -1, 1);
    const view = vec4.transformMat4(
      vec4.create(),
      clip,
      this.camera.matrices.inversProjection,
    );
    if (view[3] === 0) return null;
    view[0] /= view[3];
    view[1] /= view[3];
    view[2] /= view[3];
    view[3] = 1;

    const world = vec4.transformMat4(vec4.create(), view, this.camera.matrix);
    const origin = this.camera.position;
    const dir = vec3.normalize(
      vec3.create(),
      vec3.sub(
        vec3.create(),
        vec3.fromValues(world[0], world[1], world[2]),
        origin,
      ),
    );

    const t = this.#intersectRaySphere(origin, dir, this.SPHERE_RADIUS);
    if (t == null) return null;
    const hit = vec3.scaleAndAdd(vec3.create(), origin, dir, t);
    const hitDir = vec3.normalize(vec3.create(), hit);

    let bestDot = -Infinity;
    let bestInstance = null;
    for (let i = 0; i < this.instancePositions.length; i += 1) {
      const worldPos = this.#getVertexWorldPosition(i);
      const worldDir = vec3.normalize(vec3.create(), worldPos);
      const d = vec3.dot(hitDir, worldDir);
      if (d > bestDot) {
        bestDot = d;
        bestInstance = i;
      }
    }

    if (bestInstance == null) return null;
    const baseIndex =
      this.instanceItemIndices?.[bestInstance] != null
        ? this.instanceItemIndices[bestInstance]
        : bestInstance % Math.max(1, this.baseItems.length);
    const item = this.baseItems[baseIndex];
    const centerWorld = this.#getVertexWorldPosition(bestInstance);
    const centerScreen = this.#projectWorldToCanvas(centerWorld);

    return {
      baseIndex,
      instanceIndex: bestInstance,
      item,
      center: centerScreen,
    };
  }

  #intersectRaySphere(origin, dir, radius) {
    const b = 2 * vec3.dot(origin, dir);
    const c = vec3.dot(origin, origin) - radius * radius;
    const discriminant = b * b - 4 * c;
    if (discriminant < 0) return null;
    const sqrt = Math.sqrt(discriminant);
    const t1 = (-b - sqrt) * 0.5;
    const t2 = (-b + sqrt) * 0.5;
    if (t1 > 0) return t1;
    if (t2 > 0) return t2;
    return null;
  }

  #projectWorldToCanvas(worldPosition) {
    const rect = this.canvas.getBoundingClientRect();
    const clip = vec4.transformMat4(
      vec4.create(),
      vec4.fromValues(worldPosition[0], worldPosition[1], worldPosition[2], 1),
      this.camera.matrices.view,
    );
    vec4.transformMat4(clip, clip, this.camera.matrices.projection);
    const w = clip[3] || 1;
    const ndcX = clip[0] / w;
    const ndcY = clip[1] / w;
    const canvasX = (ndcX * 0.5 + 0.5) * rect.width;
    const canvasY = (1 - (ndcY * 0.5 + 0.5)) * rect.height;
    return {
      x: canvasX,
      y: canvasY,
      ndc: { x: ndcX, y: ndcY },
    };
  }

  run(time = 0) {
    if (this._disposed) return;
    this.#deltaTime = Math.min(32, time - this.#time);
    this.#time = time;
    this.#deltaFrames = this.#deltaTime / this.TARGET_FRAME_DURATION;
    this.#frames += this.#deltaFrames;

    this.#animate(this.#deltaTime);
    this.#render();

    this._raf = requestAnimationFrame((t) => this.run(t));
  }

  destroy() {
    this._disposed = true;
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this.control?.destroy) this.control.destroy();
  }

  #init(onInit) {
    this.gl = this.canvas.getContext("webgl2", {
      antialias: true,
      alpha: true,
    });
    const gl = this.gl;
    if (!gl) {
      throw new Error("No WebGL 2 context!");
    }

    this.viewportSize = vec2.fromValues(
      this.canvas.clientWidth,
      this.canvas.clientHeight,
    );
    this.drawBufferSize = vec2.clone(this.viewportSize);

    this.discProgram = createProgram(
      gl,
      [discVertShaderSource, discFragShaderSource],
      null,
      {
        aModelPosition: 0,
        aModelNormal: 1,
        aModelUvs: 2,
        aInstanceMatrix: 3,
        aInstanceColor: 7,
        aInstanceItemIndex: 8,
      },
    );
    this.planetProgram = createProgram(
      gl,
      [planetVertShaderSource, planetFragShaderSource],
      null,
      {
        aModelPosition: 0,
        aModelNormal: 1,
        aModelUvs: 2,
        aInstanceMatrix: 3,
        aInstanceColor: 7,
        aInstanceItemIndex: 8,
      },
    );
    this.sunProgram = createProgram(
      gl,
      [sunVertShaderSource, sunFragShaderSource],
      null,
      {
        aModelPosition: 0,
        aModelUvs: 2,
      },
    );

    this.discLocations = {
      aModelPosition: gl.getAttribLocation(this.discProgram, "aModelPosition"),
      aModelUvs: gl.getAttribLocation(this.discProgram, "aModelUvs"),
      aInstanceMatrix: gl.getAttribLocation(
        this.discProgram,
        "aInstanceMatrix",
      ),
      aInstanceColor: gl.getAttribLocation(this.discProgram, "aInstanceColor"),
      aInstanceItemIndex: gl.getAttribLocation(
        this.discProgram,
        "aInstanceItemIndex",
      ),
      uWorldMatrix: gl.getUniformLocation(this.discProgram, "uWorldMatrix"),
      uViewMatrix: gl.getUniformLocation(this.discProgram, "uViewMatrix"),
      uProjectionMatrix: gl.getUniformLocation(
        this.discProgram,
        "uProjectionMatrix",
      ),
      uCameraPosition: gl.getUniformLocation(
        this.discProgram,
        "uCameraPosition",
      ),
      uScaleFactor: gl.getUniformLocation(this.discProgram, "uScaleFactor"),
      uRotationAxisVelocity: gl.getUniformLocation(
        this.discProgram,
        "uRotationAxisVelocity",
      ),
      uTex: gl.getUniformLocation(this.discProgram, "uTex"),
      uFrames: gl.getUniformLocation(this.discProgram, "uFrames"),
      uItemCount: gl.getUniformLocation(this.discProgram, "uItemCount"),
      uAtlasSize: gl.getUniformLocation(this.discProgram, "uAtlasSize"),
      uPointer: gl.getUniformLocation(this.discProgram, "uPointer"),
    };
    this.planetLocations = {
      aModelPosition: gl.getAttribLocation(
        this.planetProgram,
        "aModelPosition",
      ),
      aModelNormal: gl.getAttribLocation(this.planetProgram, "aModelNormal"),
      aModelUvs: gl.getAttribLocation(this.planetProgram, "aModelUvs"),
      aInstanceMatrix: gl.getAttribLocation(
        this.planetProgram,
        "aInstanceMatrix",
      ),
      aInstanceColor: gl.getAttribLocation(
        this.planetProgram,
        "aInstanceColor",
      ),
      aInstanceItemIndex: gl.getAttribLocation(
        this.planetProgram,
        "aInstanceItemIndex",
      ),
      uWorldMatrix: gl.getUniformLocation(this.planetProgram, "uWorldMatrix"),
      uViewMatrix: gl.getUniformLocation(this.planetProgram, "uViewMatrix"),
      uProjectionMatrix: gl.getUniformLocation(
        this.planetProgram,
        "uProjectionMatrix",
      ),
      uCameraPosition: gl.getUniformLocation(
        this.planetProgram,
        "uCameraPosition",
      ),
      uTex: gl.getUniformLocation(this.planetProgram, "uTex"),
      uFrames: gl.getUniformLocation(this.planetProgram, "uFrames"),
      uItemCount: gl.getUniformLocation(this.planetProgram, "uItemCount"),
      uAtlasSize: gl.getUniformLocation(this.planetProgram, "uAtlasSize"),
    };
    this.sunLocations = {
      aModelPosition: gl.getAttribLocation(this.sunProgram, "aModelPosition"),
      aModelUvs: gl.getAttribLocation(this.sunProgram, "aModelUvs"),
      uWorldMatrix: gl.getUniformLocation(this.sunProgram, "uWorldMatrix"),
      uViewMatrix: gl.getUniformLocation(this.sunProgram, "uViewMatrix"),
      uProjectionMatrix: gl.getUniformLocation(
        this.sunProgram,
        "uProjectionMatrix",
      ),
      uScale: gl.getUniformLocation(this.sunProgram, "uScale"),
      uTime: gl.getUniformLocation(this.sunProgram, "uTime"),
      uIntensity: gl.getUniformLocation(this.sunProgram, "uIntensity"),
      uColor: gl.getUniformLocation(this.sunProgram, "uColor"),
      uCoreColor: gl.getUniformLocation(this.sunProgram, "uCoreColor"),
    };

    this.discGeo = new DiscGeometry(56, 1);
    this.discBuffers = this.discGeo.data;
    this.discVAO = makeVertexArray(
      gl,
      [
        [
          makeBuffer(gl, this.discBuffers.vertices, gl.STATIC_DRAW),
          this.discLocations.aModelPosition,
          3,
        ],
        [
          makeBuffer(gl, this.discBuffers.uvs, gl.STATIC_DRAW),
          this.discLocations.aModelUvs,
          2,
        ],
      ],
      this.discBuffers.indices,
    );

    this.planetGeo = new IcosahedronGeometry()
      .subdivide(3)
      .spherize(1)
      .computeSphericalUVs();
    this.planetBuffers = this.planetGeo.data;
    this.planetVAO = makeVertexArray(
      gl,
      [
        [
          makeBuffer(gl, this.planetBuffers.vertices, gl.STATIC_DRAW),
          this.planetLocations.aModelPosition,
          3,
        ],
        [
          makeBuffer(gl, this.planetBuffers.normals, gl.STATIC_DRAW),
          this.planetLocations.aModelNormal,
          3,
        ],
        [
          makeBuffer(gl, this.planetBuffers.uvs, gl.STATIC_DRAW),
          this.planetLocations.aModelUvs,
          2,
        ],
      ],
      this.planetBuffers.indices,
    );

    this.sunGeo = new DiscGeometry(96, 1);
    this.sunBuffers = this.sunGeo.data;
    this.sunVAO = makeVertexArray(
      gl,
      [
        [
          makeBuffer(gl, this.sunBuffers.vertices, gl.STATIC_DRAW),
          this.sunLocations.aModelPosition,
          3,
        ],
        [
          makeBuffer(gl, this.sunBuffers.uvs, gl.STATIC_DRAW),
          this.sunLocations.aModelUvs,
          2,
        ],
      ],
      this.sunBuffers.indices,
    );

    this.baseItemCount = Math.max(1, this.baseItems.length);
    const instanceCount = this.baseItemCount * 2;
    this.instancePositions = this.#buildSpherePositions(instanceCount);
    this.instanceItemIndices = this.#assignItemPairsToPositions(
      this.baseItemCount,
      this.instancePositions,
    );
    this.INSTANCE_COUNT = this.instancePositions.length;
    this.#initDiscInstances(this.INSTANCE_COUNT);

    this.worldMatrix = mat4.create();
    this.#initTexture();

    this.control = new ArcballControl(this.canvas, (deltaTime) =>
      this.#onControlUpdate(deltaTime),
    );
    this.#setInitialActiveItem();

    this.#updateCameraMatrix();
    this.#updateProjectionMatrix(gl);
    this.resize();

    if (onInit) onInit(this);
  }

  #buildSpherePositions(count) {
    const positions = [];
    if (count <= 0) return positions;

    if (count === 1) {
      positions.push(vec3.fromValues(0, 0, this.SPHERE_RADIUS));
    } else {
      const goldenAngle = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < count; i += 1) {
        const t = (i + 0.5) / count;
        const y = 1 - t * 2;
        const radius = Math.sqrt(1 - y * y);
        const theta = goldenAngle * i;
        const x = Math.cos(theta) * radius;
        const z = Math.sin(theta) * radius;
        positions.push(
          vec3.fromValues(
            x * this.SPHERE_RADIUS,
            y * this.SPHERE_RADIUS,
            z * this.SPHERE_RADIUS,
          ),
        );
      }
    }

    return positions;
  }

  #assignItemPairsToPositions(itemCount, positions) {
    const total = positions.length;
    const assignment = new Array(total).fill(null);
    const available = new Set(Array.from({ length: total }, (_, i) => i));
    const pairs = [];

    for (let i = 0; i < total - 1; i += 1) {
      for (let j = i + 1; j < total; j += 1) {
        const dist = vec3.squaredDistance(positions[i], positions[j]);
        pairs.push({ i, j, dist });
      }
    }

    pairs.sort((a, b) => b.dist - a.dist);

    for (let itemIndex = 0; itemIndex < itemCount; itemIndex += 1) {
      let chosen = null;
      for (const pair of pairs) {
        if (available.has(pair.i) && available.has(pair.j)) {
          chosen = pair;
          break;
        }
      }
      if (!chosen) break;
      assignment[chosen.i] = itemIndex;
      assignment[chosen.j] = itemIndex;
      available.delete(chosen.i);
      available.delete(chosen.j);
    }

    if (available.size > 0) {
      const fallback = Array.from({ length: itemCount }, (_, i) => i);
      let cursor = 0;
      for (const index of available) {
        assignment[index] = fallback[cursor % fallback.length];
        cursor += 1;
      }
    }

    return assignment;
  }

  #initTexture() {
    const gl = this.gl;
    this.tex = createAndSetupTexture(
      gl,
      gl.LINEAR,
      gl.LINEAR,
      gl.CLAMP_TO_EDGE,
      gl.CLAMP_TO_EDGE,
    );

    const itemCount = Math.max(1, this.baseItems.length);
    this.atlasSize = Math.ceil(Math.sqrt(itemCount));
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const cellSize = 512;

    canvas.width = this.atlasSize * cellSize;
    canvas.height = this.atlasSize * cellSize;

    Promise.all(
      this.baseItems.map(
        (item) =>
          new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => resolve(img);
            img.src = item.image;
          }),
      ),
    ).then((images) => {
      images.forEach((img, i) => {
        const x = (i % this.atlasSize) * cellSize;
        const y = Math.floor(i / this.atlasSize) * cellSize;
        ctx.drawImage(img, x, y, cellSize, cellSize);
      });

      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        canvas,
      );
      gl.generateMipmap(gl.TEXTURE_2D);
    });
  }

  #initDiscInstances(count) {
    const gl = this.gl;
    const itemCount = Math.max(1, this.baseItems.length);
    this.discInstances = {
      matricesArray: new Float32Array(count * 16),
      matrices: [],
      buffer: gl.createBuffer(),
      colorsArray: new Float32Array(count * 3),
      colorBuffer: gl.createBuffer(),
      itemIndexArray: new Float32Array(count),
      itemIndexBuffer: gl.createBuffer(),
    };
    for (let i = 0; i < count; ++i) {
      const instanceMatrixArray = new Float32Array(
        this.discInstances.matricesArray.buffer,
        i * 16 * 4,
        16,
      );
      instanceMatrixArray.set(mat4.create());
      this.discInstances.matrices.push(instanceMatrixArray);

      const baseIndex =
        this.instanceItemIndices?.[i] != null
          ? this.instanceItemIndices[i]
          : i % itemCount;
      const item = this.baseItems[baseIndex];
      const color = hexToRgb01(item?.color) || { r: 0.49, g: 0.52, b: 0.58 };
      const colorOffset = i * 3;
      this.discInstances.colorsArray[colorOffset] = color.r;
      this.discInstances.colorsArray[colorOffset + 1] = color.g;
      this.discInstances.colorsArray[colorOffset + 2] = color.b;
      this.discInstances.itemIndexArray[i] = baseIndex;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.discInstances.matricesArray.byteLength,
      gl.DYNAMIC_DRAW,
    );

    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.colorBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.discInstances.colorsArray,
      gl.STATIC_DRAW,
    );

    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.itemIndexBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.discInstances.itemIndexArray,
      gl.STATIC_DRAW,
    );

    const attachInstances = (vao, locations) => {
      gl.bindVertexArray(vao);

      gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
      const mat4AttribSlotCount = 4;
      const bytesPerMatrix = 16 * 4;
      for (let j = 0; j < mat4AttribSlotCount; ++j) {
        const loc = locations.aInstanceMatrix + j;
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(
          loc,
          4,
          gl.FLOAT,
          false,
          bytesPerMatrix,
          j * 4 * 4,
        );
        gl.vertexAttribDivisor(loc, 1);
      }

      const colorLoc = locations.aInstanceColor;
      if (colorLoc !== -1) {
        gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.colorBuffer);
        gl.enableVertexAttribArray(colorLoc);
        gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);
        gl.vertexAttribDivisor(colorLoc, 1);
      }

      const itemLoc = locations.aInstanceItemIndex;
      if (itemLoc !== -1) {
        gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.itemIndexBuffer);
        gl.enableVertexAttribArray(itemLoc);
        gl.vertexAttribPointer(itemLoc, 1, gl.FLOAT, false, 0, 0);
        gl.vertexAttribDivisor(itemLoc, 1);
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, null);
      gl.bindVertexArray(null);
    };

    attachInstances(this.discVAO, this.discLocations);
    attachInstances(this.planetVAO, this.planetLocations);

    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindVertexArray(null);
  }

  #animate(deltaTime) {
    const gl = this.gl;
    this.control.update(deltaTime, this.TARGET_FRAME_DURATION);

    const sunEase = Math.min(
      1,
      (deltaTime / this.TARGET_FRAME_DURATION) * 0.12,
    );
    this.sunIntensity +=
      (this.sunTargetIntensity - this.sunIntensity) * sunEase;
    const timeSeconds = this.#time * 0.001;
    this.sunScaleDynamic =
      this.sunScale * (1 + 0.03 * Math.sin(timeSeconds * 1.4));

    const positions = this.instancePositions.map((p) =>
      vec3.transformQuat(vec3.create(), p, this.control.orientation),
    );
    this.instanceWorldPositions = positions;
    const baseScale = this.variant === "sphere" ? 0.33 : 0.25;
    const SCALE_INTENSITY = 0.6;
    positions.forEach((p, ndx) => {
      const s =
        (Math.abs(p[2]) / this.SPHERE_RADIUS) * SCALE_INTENSITY +
        (1 - SCALE_INTENSITY);
      const finalScale = s * baseScale;
      const matrix = mat4.create();

      if (this.variant === "sphere") {
        mat4.fromTranslation(matrix, p);
        const spin = timeSeconds * 0.35 + ndx * 0.65;
        mat4.rotateY(matrix, matrix, spin);
        mat4.rotateX(matrix, matrix, 0.08 * Math.sin(timeSeconds * 0.6 + ndx));
        mat4.scale(matrix, matrix, [finalScale, finalScale, finalScale]);
      } else {
        mat4.multiply(
          matrix,
          matrix,
          mat4.fromTranslation(mat4.create(), vec3.negate(vec3.create(), p)),
        );
        mat4.multiply(
          matrix,
          matrix,
          mat4.targetTo(mat4.create(), [0, 0, 0], p, [0, 1, 0]),
        );
        mat4.multiply(
          matrix,
          matrix,
          mat4.fromScaling(mat4.create(), [finalScale, finalScale, finalScale]),
        );
        mat4.multiply(
          matrix,
          matrix,
          mat4.fromTranslation(mat4.create(), [0, 0, -this.SPHERE_RADIUS]),
        );
      }

      mat4.copy(this.discInstances.matrices[ndx], matrix);
    });

    gl.bindBuffer(gl.ARRAY_BUFFER, this.discInstances.buffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.discInstances.matricesArray);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    this.smoothRotationVelocity = this.control.rotationVelocity;
  }

  #render() {
    const gl = this.gl;
    const isSphere = this.variant === "sphere";
    gl.useProgram(isSphere ? this.planetProgram : this.discProgram);

    gl.enable(gl.CULL_FACE);
    gl.enable(gl.DEPTH_TEST);

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    if (isSphere) {
      gl.uniformMatrix4fv(
        this.planetLocations.uWorldMatrix,
        false,
        this.worldMatrix,
      );
      gl.uniformMatrix4fv(
        this.planetLocations.uViewMatrix,
        false,
        this.camera.matrices.view,
      );
      gl.uniformMatrix4fv(
        this.planetLocations.uProjectionMatrix,
        false,
        this.camera.matrices.projection,
      );
      gl.uniform3f(
        this.planetLocations.uCameraPosition,
        this.camera.position[0],
        this.camera.position[1],
        this.camera.position[2],
      );

      gl.uniform1i(this.planetLocations.uItemCount, this.baseItems.length);
      gl.uniform1i(this.planetLocations.uAtlasSize, this.atlasSize);
      gl.uniform1f(this.planetLocations.uFrames, this.#frames);
      gl.uniform1i(this.planetLocations.uTex, 0);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.tex);

      gl.bindVertexArray(this.planetVAO);
      gl.drawElementsInstanced(
        gl.TRIANGLES,
        this.planetBuffers.indices.length,
        gl.UNSIGNED_SHORT,
        0,
        this.INSTANCE_COUNT,
      );
    } else {
      gl.uniformMatrix4fv(
        this.discLocations.uWorldMatrix,
        false,
        this.worldMatrix,
      );
      gl.uniformMatrix4fv(
        this.discLocations.uViewMatrix,
        false,
        this.camera.matrices.view,
      );
      gl.uniformMatrix4fv(
        this.discLocations.uProjectionMatrix,
        false,
        this.camera.matrices.projection,
      );
      gl.uniform3f(
        this.discLocations.uCameraPosition,
        this.camera.position[0],
        this.camera.position[1],
        this.camera.position[2],
      );
      gl.uniform4f(
        this.discLocations.uRotationAxisVelocity,
        this.control.rotationAxis[0],
        this.control.rotationAxis[1],
        this.control.rotationAxis[2],
        this.smoothRotationVelocity * 1.1,
      );

      gl.uniform1i(this.discLocations.uItemCount, this.baseItems.length);
      gl.uniform1i(this.discLocations.uAtlasSize, this.atlasSize);
      gl.uniform2f(
        this.discLocations.uPointer,
        this.pointer[0],
        this.pointer[1],
      );

      gl.uniform1f(this.discLocations.uFrames, this.#frames);
      gl.uniform1f(this.discLocations.uScaleFactor, this.scaleFactor);
      gl.uniform1i(this.discLocations.uTex, 0);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.tex);

      gl.bindVertexArray(this.discVAO);
      gl.drawElementsInstanced(
        gl.TRIANGLES,
        this.discBuffers.indices.length,
        gl.UNSIGNED_SHORT,
        0,
        this.INSTANCE_COUNT,
      );
    }

    if (this.sunIntensity > 0.01) {
      gl.useProgram(this.sunProgram);
      gl.bindVertexArray(this.sunVAO);

      gl.disable(gl.CULL_FACE);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
      gl.depthMask(false);

      gl.uniformMatrix4fv(
        this.sunLocations.uWorldMatrix,
        false,
        this.worldMatrix,
      );
      gl.uniformMatrix4fv(
        this.sunLocations.uViewMatrix,
        false,
        this.camera.matrices.view,
      );
      gl.uniformMatrix4fv(
        this.sunLocations.uProjectionMatrix,
        false,
        this.camera.matrices.projection,
      );
      gl.uniform1f(
        this.sunLocations.uScale,
        this.sunScaleDynamic || this.sunScale,
      );
      gl.uniform1f(this.sunLocations.uTime, this.#time * 0.001);
      gl.uniform1f(this.sunLocations.uIntensity, this.sunIntensity);
      gl.uniform3f(
        this.sunLocations.uColor,
        this.sunColor.r,
        this.sunColor.g,
        this.sunColor.b,
      );
      gl.uniform3f(
        this.sunLocations.uCoreColor,
        this.sunCoreColor.r,
        this.sunCoreColor.g,
        this.sunCoreColor.b,
      );

      gl.drawElements(
        gl.TRIANGLES,
        this.sunBuffers.indices.length,
        gl.UNSIGNED_SHORT,
        0,
      );

      gl.depthMask(true);
      gl.disable(gl.BLEND);
      gl.enable(gl.CULL_FACE);
    }
  }

  #updateCameraMatrix() {
    mat4.targetTo(
      this.camera.matrix,
      this.camera.position,
      [0, 0, 0],
      this.camera.up,
    );
    mat4.invert(this.camera.matrices.view, this.camera.matrix);
  }

  #updateProjectionMatrix(gl) {
    this.camera.aspect = gl.canvas.clientWidth / gl.canvas.clientHeight;
    const height = this.SPHERE_RADIUS * 0.35;
    const distance = this.camera.position[2];
    if (this.camera.aspect > 1) {
      this.camera.fov = 2 * Math.atan(height / distance);
    } else {
      this.camera.fov = 2 * Math.atan(height / this.camera.aspect / distance);
    }
    mat4.perspective(
      this.camera.matrices.projection,
      this.camera.fov,
      this.camera.aspect,
      this.camera.near,
      this.camera.far,
    );
    mat4.invert(
      this.camera.matrices.inversProjection,
      this.camera.matrices.projection,
    );
  }

  #onControlUpdate(deltaTime) {
    const timeScale = deltaTime / this.TARGET_FRAME_DURATION + 0.0001;
    let damping = 5 / timeScale;
    let cameraTargetZ = this.baseCameraDistance;

    const isMoving =
      this.control.isPointerDown ||
      Math.abs(this.smoothRotationVelocity) > 0.01;

    if (isMoving !== this.movementActive) {
      this.movementActive = isMoving;
      this.onMovementChange(isMoving);
    }

    if (isMoving) {
      this.sunHoldUntil = this.#time + 850;
    }
    this.sunTargetIntensity = this.#time < this.sunHoldUntil ? 1 : 0;

    const nearestVertexIndex = this.#findNearestVertexIndex();
    const baseIndex =
      this.instanceItemIndices?.[nearestVertexIndex] != null
        ? this.instanceItemIndices[nearestVertexIndex]
        : nearestVertexIndex % Math.max(1, this.baseItems.length);

    this.nearestVertexIndex = nearestVertexIndex;
    if (baseIndex !== this.activeBaseIndex) {
      this.activeBaseIndex = baseIndex;
      this.onActiveItemChange(baseIndex, this.baseItems[baseIndex]);
    }

    const snapDirection = vec3.normalize(
      vec3.create(),
      this.#getVertexWorldPosition(nearestVertexIndex),
    );
    this.control.snapTargetDirection = snapDirection;

    if (this.control.isPointerDown) {
      const dragZoom =
        this.variant === "sphere"
          ? this.control.rotationVelocity * 24 + 1.25
          : this.control.rotationVelocity * 80 + 2.5;
      cameraTargetZ += dragZoom;
      damping = 7 / timeScale;
    }

    const minCameraZ =
      this.variant === "sphere"
        ? this.baseCameraDistance
        : this.baseCameraDistance * 0.85;
    const maxCameraZ =
      this.variant === "sphere"
        ? this.baseCameraDistance + 1.6 * this.scaleFactor
        : this.baseCameraDistance + 3.8;
    cameraTargetZ = Math.min(maxCameraZ, Math.max(minCameraZ, cameraTargetZ));

    this.camera.position[2] +=
      (cameraTargetZ - this.camera.position[2]) / damping;
    this.#updateCameraMatrix();
  }

  #findNearestVertexIndex() {
    const n = this.control.snapDirection;
    const inversOrientation = quat.conjugate(
      quat.create(),
      this.control.orientation,
    );
    const nt = vec3.transformQuat(vec3.create(), n, inversOrientation);

    let maxD = -1;
    let nearestVertexIndex;
    for (let i = 0; i < this.instancePositions.length; ++i) {
      const d = vec3.dot(nt, this.instancePositions[i]);
      if (d > maxD) {
        maxD = d;
        nearestVertexIndex = i;
      }
    }
    return nearestVertexIndex;
  }

  #setInitialActiveItem() {
    if (!this.initialActiveItemId || !this.baseItems.length) return;

    const baseIndex = this.baseItems.findIndex(
      (item) => item?.id === this.initialActiveItemId,
    );
    if (baseIndex < 0) return;

    let targetInstanceIndex = null;
    let maxZ = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < this.instancePositions.length; i += 1) {
      if (this.instanceItemIndices?.[i] !== baseIndex) continue;
      const z = this.instancePositions[i][2];
      if (z > maxZ) {
        maxZ = z;
        targetInstanceIndex = i;
      }
    }
    if (targetInstanceIndex == null) return;

    const targetDirection = vec3.normalize(
      vec3.create(),
      this.instancePositions[targetInstanceIndex],
    );
    const initialOrientation = quat.rotationTo(
      quat.create(),
      targetDirection,
      this.control.snapDirection,
    );
    quat.copy(this.control.orientation, initialOrientation);
    quat.identity(this.control.pointerRotation);
    quat.identity(this.control._combinedQuat);
    this.control._rotationVelocity = 0;
    this.control.rotationVelocity = 0;

    this.nearestVertexIndex = targetInstanceIndex;
    this.activeBaseIndex = baseIndex;
    this.onActiveItemChange(baseIndex, this.baseItems[baseIndex]);
  }

  #getVertexWorldPosition(index) {
    const nearestVertexPos = this.instancePositions[index];
    return vec3.transformQuat(
      vec3.create(),
      nearestVertexPos,
      this.control.orientation,
    );
  }
}

const defaultItems = [
  {
    image: "https://picsum.photos/900/900?grayscale",
    id: "sample",
    title: "",
    description: "",
    color: "#7c8493",
  },
];

export default function InfiniteMenu({
  items = [],
  scale = 1.0,
  variant = "disc",
  initialActiveItemId = null,
  onItemClick = null,
  onActiveItemChange = null,
  onMovementChange = null,
}) {
  const canvasRef = useRef(null);
  const onItemClickRef = useRef(onItemClick);
  const onActiveItemChangeRef = useRef(onActiveItemChange);
  const onMovementChangeRef = useRef(onMovementChange);

  const resolvedItems = useMemo(
    () => (items.length ? items : defaultItems),
    [items],
  );

  useEffect(() => {
    onItemClickRef.current = onItemClick;
  }, [onItemClick]);

  useEffect(() => {
    onActiveItemChangeRef.current = onActiveItemChange;
  }, [onActiveItemChange]);

  useEffect(() => {
    onMovementChangeRef.current = onMovementChange;
  }, [onMovementChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    let sketch;
    const clickState = { isDown: false, startX: 0, startY: 0, moved: false };

    if (canvas) {
      sketch = new InfiniteGridMenu(
        canvas,
        resolvedItems,
        (...args) => onActiveItemChangeRef.current?.(...args),
        (...args) => onMovementChangeRef.current?.(...args),
        (sk) => sk.run(),
        scale,
        variant,
        initialActiveItemId,
      );
      sketch.setPointer(0.5, 0.5);
    }

    const handleResize = () => {
      if (sketch) {
        sketch.resize();
      }
    };

    const setPointerFromEvent = (event) => {
      if (!sketch || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = 1 - (event.clientY - rect.top) / rect.height;
      sketch.setPointer(x, y);
    };

    const handlePointerMove = (event) => {
      setPointerFromEvent(event);
      if (!clickState.isDown) return;
      const dx = event.clientX - clickState.startX;
      const dy = event.clientY - clickState.startY;
      if (dx * dx + dy * dy > 144) {
        clickState.moved = true;
      }
    };

    const handlePointerDown = (event) => {
      clickState.isDown = true;
      clickState.moved = false;
      clickState.startX = event.clientX;
      clickState.startY = event.clientY;
      setPointerFromEvent(event);
    };

    const handlePointerUp = (event) => {
      if (!sketch || !canvas) return;
      const clickHandler = onItemClickRef.current;
      if (clickHandler && clickState.isDown && !clickState.moved) {
        const picked = sketch.pick(event.clientX, event.clientY);
        if (picked?.item) {
          clickHandler(picked);
        }
      }
      clickState.isDown = false;
      clickState.moved = false;
    };

    const handlePointerLeave = () => {
      if (sketch) {
        sketch.setPointer(0.5, 0.5);
      }
      clickState.isDown = false;
      clickState.moved = false;
    };

    if (canvas) {
      canvas.addEventListener("pointermove", handlePointerMove);
      canvas.addEventListener("pointerdown", handlePointerDown);
      canvas.addEventListener("pointerup", handlePointerUp);
      canvas.addEventListener("pointerleave", handlePointerLeave);
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      if (canvas) {
        canvas.removeEventListener("pointermove", handlePointerMove);
        canvas.removeEventListener("pointerdown", handlePointerDown);
        canvas.removeEventListener("pointerup", handlePointerUp);
        canvas.removeEventListener("pointerleave", handlePointerLeave);
      }
      window.removeEventListener("resize", handleResize);
      if (sketch) sketch.destroy();
    };
  }, [resolvedItems, scale, variant, initialActiveItemId]);

  return (
    <div className="infinite-menu">
      <canvas
        id="infinite-grid-menu-canvas"
        className="infinite-menu__canvas"
        ref={canvasRef}
      />
    </div>
  );
}
